import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import {
  fileRecordVersionKey,
  pickFileRecordMediaURL,
  useStickyMediaSrc,
} from "@cohesive/ui-components";
import { useState } from "react";
import { LoadingOverlay } from "../LoadingOverlay";
import { getFileRecordName } from "../contentType";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";

/**
 * docx / pptx / doc / ppt / odt preview. Spreadsheets (csv / xls / xlsx)
 * render natively in `SpreadsheetContent` and must not route here.
 *
 * Uses Google's public `gview` iframe viewer — no Google account required,
 * no backend work needed, handles the full office stack. Tradeoff: the
 * source URL is fetched by Google's infrastructure (not the browser), so
 * the file is temporarily visible to Google's edge. Acceptable for org
 * uploads; if we ever need stricter isolation we'd swap to a client-side
 * renderer (mammoth.js) or a server-side PDF conversion step.
 */
export function OfficeContent({ record }: { record: FileRecord }) {
  const [loaded, setLoaded] = useState(false);
  const { previewMediaUrl } = useFileRecordVersionPreview();
  const mediaUrl =
    previewMediaUrl ??
    pickFileRecordMediaURL(record, "full") ??
    pickFileRecordMediaURL(record, "thumbnail");
  // Google's viewer fetched the file once; a renewed link would only make
  // it start over. The frame keys on the version.
  const { src } = useStickyMediaSrc(mediaUrl);

  if (!mediaUrl || !src) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        Document not available.
      </div>
    );
  }

  return (
    <div className="relative h-full w-full bg-muted/20">
      <iframe
        key={previewMediaUrl ?? fileRecordVersionKey(record)}
        src={`https://docs.google.com/gview?url=${encodeURIComponent(src)}&embedded=true`}
        title={getFileRecordName(record)}
        className={cn(
          "w-full h-full border-0 transition-opacity duration-300",
          loaded ? "opacity-100" : "opacity-0",
        )}
        onLoad={() => setTimeout(() => setLoaded(true), 300)}
      />
      {!loaded && <LoadingOverlay />}
    </div>
  );
}
